import { useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// Editorial baseline field: floating label + slow-drawing focus underline.
export const LineField = ({ label, value, onChange, type = "text", textarea = false, required = false, testId, tone = "onLight" }) => {
  const [focused, setFocused] = useState(false);
  const active = focused || (value != null && String(value).length > 0);
  const dark = tone === "onDark";
  const shared = {
    value,
    onChange,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    required,
    "data-testid": testId,
    className: `peer relative z-10 w-full resize-none border-0 bg-transparent px-0 pb-3 pt-1 font-serif text-xl font-light focus:outline-none md:text-2xl ${dark ? "text-gaia-bg" : "text-gaia-charcoal"}`,
  };
  return (
    <div className="relative pt-7">
      <motion.span
        className={`pointer-events-none absolute left-0 top-0 origin-left font-sans uppercase tracking-[0.26em] ${dark ? "text-gaia-bg/50" : "text-gaia-charcoal/45"}`}
        initial={false}
        animate={{ y: active ? 0 : 34, fontSize: active ? "11px" : "13px" }}
        transition={{ duration: 0.5, ease: EASE }}
      >
        {label}
      </motion.span>
      {textarea ? <textarea rows={3} {...shared} /> : <input type={type} {...shared} />}
      <span className={`absolute bottom-0 left-0 h-px w-full ${dark ? "bg-gaia-bg/25" : "bg-gaia-charcoal/20"}`} />
      <motion.span
        className="absolute bottom-0 left-0 h-px w-full origin-left bg-gaia-terracotta"
        initial={false}
        animate={{ scaleX: focused ? 1 : 0 }}
        transition={{ duration: 0.6, ease: EASE }}
      />
    </div>
  );
};

// Sharp-edged button with a slow "breath" fill rising from the base.
export const FillButton = ({ children, variant = "dark", size = "md", className = "", ...props }) => {
  const light = variant === "light";
  const pad = size === "sm" ? "px-8 py-3" : "px-10 py-4";
  return (
    <button
      {...props}
      className={`group relative shrink-0 overflow-hidden border ${pad} font-sans text-[11px] uppercase tracking-[0.24em] disabled:opacity-40 ${
        light ? "border-gaia-bg/50 text-gaia-bg" : "border-gaia-charcoal text-gaia-charcoal"
      } ${className}`}
    >
      <span
        aria-hidden
        className={`absolute inset-0 origin-bottom scale-y-0 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100 ${
          light ? "bg-gaia-bg" : "bg-gaia-charcoal"
        }`}
      />
      <span
        className={`relative z-10 block transition-colors duration-500 ${
          light ? "group-hover:text-gaia-charcoal" : "group-hover:text-gaia-bg"
        }`}
      >
        {children}
      </span>
    </button>
  );
};
